import type { ReactNode } from "react";

import { cn } from "@/lib/utils";

// ===========================================================================
// Las piezas de la propuesta de diseño (`referencia/propuesta-diseno`).
//
// Se traducen a Tailwind conservando lo que las define: el tile con su cinta
// de color a la izquierda, el valor grande en el color del estado, la barra de
// avance y el sparkline. La paleta es la de `fusion.css`:
//
//   ok   #0E9F6E   warn #C77A08   bad  #D0342C
//   loan #2E6FE0   acento #0FB5CE
//
// El estado NUNCA se dice solo con color: siempre lleva su palabra al lado.
// ===========================================================================

export type Tono = "ok" | "warn" | "bad" | "loan" | "acc" | "neutro";

const CINTA: Record<Tono, string> = {
  ok: "bg-emerald-600",
  warn: "bg-amber-600",
  bad: "bg-red-600",
  loan: "bg-blue-600",
  acc: "bg-cyan-500",
  neutro: "bg-slate-400",
};

const VALOR: Record<Tono, string> = {
  ok: "text-emerald-600 dark:text-emerald-500",
  warn: "text-amber-600 dark:text-amber-500",
  bad: "text-red-600 dark:text-red-500",
  loan: "text-blue-600 dark:text-blue-500",
  acc: "text-cyan-700 dark:text-cyan-400",
  neutro: "text-foreground",
};

/** Un tile del arranque: título, valor grande y un pie que lo explica. */
export function Tile({
  titulo,
  subtitulo,
  valor,
  unidad,
  tono = "acc",
  pie,
  ancho,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  valor?: ReactNode;
  unidad?: string;
  tono?: Tono;
  pie?: ReactNode;
  ancho?: 1 | 2;
  children?: ReactNode;
}) {
  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-lg border bg-card p-4 shadow-sm",
        ancho === 2 && "sm:col-span-2",
      )}
    >
      {/* La cinta: 3px del color del estado, pegada al borde izquierdo. */}
      <span className={cn("absolute inset-y-0 left-0 w-[3px]", CINTA[tono])} />
      <div className="font-semibold text-[12.5px] leading-tight">{titulo}</div>
      {subtitulo ? (
        <div className="mt-0.5 text-[11px] text-muted-foreground">
          {subtitulo}
        </div>
      ) : null}
      {valor !== undefined ? (
        <div
          className={cn(
            "mt-2 font-bold text-[32px] tabular-nums leading-none tracking-tight",
            VALOR[tono],
          )}
        >
          {valor}
          {unidad ? (
            <span className="ml-0.5 font-semibold text-muted-foreground text-sm">
              {unidad}
            </span>
          ) : null}
        </div>
      ) : null}
      {children}
      {pie ? (
        <div className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          {pie}
        </div>
      ) : null}
    </div>
  );
}

/** La barra de avance de la propuesta: 7px, redondeada, sobre el gris de la línea. */
export function Pista({
  porcentaje,
  tono = "acc",
}: {
  porcentaje: number;
  tono?: Tono;
}) {
  return (
    <div className="mt-2 h-2 overflow-hidden rounded-full bg-muted">
      <div
        className={cn("h-full rounded-full", CINTA[tono])}
        style={{ width: `${Math.min(100, Math.max(0, porcentaje))}%` }}
      />
    </div>
  );
}

/**
 * El sparkline: una barra por día.
 *
 * Sin ejes ni cuadrícula, porque no se lee un valor concreto sino la forma: si
 * la lectura avanza o se paró. Los últimos días van en el color de acento para
 * que se vea dónde estamos.
 */
export function Sparkline({
  serie,
  destacarUltimos = 3,
}: {
  serie: Array<{ dia: string; cuantos: number }>;
  destacarUltimos?: number;
}) {
  if (serie.length === 0) {
    return (
      <p className="mt-3 text-[11px] text-muted-foreground">
        Todavía no se ha leído ninguna.
      </p>
    );
  }
  const mayor = Math.max(...serie.map((s) => s.cuantos), 1);
  const desde = serie.length - destacarUltimos;

  // Con uno o dos días no hay forma que mirar: una barra ocupando todo el
  // ancho no es un gráfico, es un rectángulo. Se dice el número y ya.
  if (serie.length < 3) {
    const total = serie.reduce((n, s) => n + s.cuantos, 0);
    return (
      <div className="mt-2.5 flex h-[34px] items-end">
        <span className="font-bold text-[28px] text-cyan-700 tabular-nums leading-none dark:text-cyan-400">
          {total.toLocaleString("es-PE")}
        </span>
        <span className="mb-0.5 ml-2 text-[11px] text-muted-foreground">
          leídas {serie.length === 1 ? "hoy" : "en dos días"}
        </span>
      </div>
    );
  }

  return (
    <div className="mt-2.5 flex h-[34px] items-end gap-[3px]">
      {serie.map((s, i) => (
        <div
          key={s.dia}
          title={`${s.dia}: ${s.cuantos.toLocaleString("es-PE")}`}
          className={cn(
            "flex-1 rounded-t-sm",
            i >= desde ? "bg-cyan-500" : "bg-cyan-200 dark:bg-cyan-900",
          )}
          style={{ height: `${Math.max(6, (s.cuantos / mayor) * 100)}%` }}
        />
      ))}
    </div>
  );
}

const PILDORA: Record<Tono, string> = {
  ok: "border-emerald-600/30 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400",
  warn: "border-amber-600/30 bg-amber-600/10 text-amber-700 dark:text-amber-400",
  bad: "border-red-600/30 bg-red-600/10 text-red-700 dark:text-red-400",
  loan: "border-blue-600/30 bg-blue-600/10 text-blue-700 dark:text-blue-400",
  acc: "border-cyan-600/30 bg-cyan-600/10 text-cyan-700 dark:text-cyan-400",
  neutro: "border-border bg-muted text-muted-foreground",
};

/**
 * Estado con punto Y palabra, dentro de una píldora.
 *
 * El color acompaña, nunca informa solo: quien no distingue el verde del ámbar
 * lee la palabra igual. La píldora es lo que hace que se lea como una etiqueta
 * de estado y no como texto corrido — así está en la lámina.
 */
export function Estado({
  tono,
  children,
}: {
  tono: Tono;
  children: ReactNode;
}) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-2.5 py-1 font-medium text-[12.5px]",
        PILDORA[tono],
      )}
    >
      <span className={cn("size-2 shrink-0 rounded-full", CINTA[tono])} />
      {children}
    </span>
  );
}

/** Una caja de la fila de abajo: cabecera con título y dato a la derecha. */
export function Caja({
  titulo,
  derecha,
  children,
}: {
  titulo: string;
  derecha?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b px-4 py-3">
        <h3 className="font-semibold text-sm">{titulo}</h3>
        {derecha ? (
          <span className="text-[11px] text-muted-foreground">{derecha}</span>
        ) : null}
      </div>
      <div className="min-h-0 flex-1 overflow-y-auto p-4">{children}</div>
    </div>
  );
}

/** Un aviso: icono, texto y color de fondo suave. Nunca solo color. */
export function Aviso({
  tono = "warn",
  children,
}: {
  tono?: "warn" | "info" | "bad";
  children: ReactNode;
}) {
  const estilo = {
    warn: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    info: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:text-sky-400",
    bad: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400",
  }[tono];

  return (
    <div
      className={cn(
        "flex items-start gap-2.5 rounded-md border p-3 text-[12px]",
        estilo,
      )}
    >
      {children}
    </div>
  );
}

/** El punto suelto, para las líneas de actividad donde no cabe una píldora. */
export function Punto({ tono }: { tono: Tono }) {
  return (
    <span className={cn("mt-1.5 size-2 shrink-0 rounded-full", CINTA[tono])} />
  );
}
